import { Box, Typography } from '@mui/material';
import { useGetArchivedPatientsInOrg, useGetArchivedUsersInOrg } from '_api/queries';
import { ConstantsNameDescriptions, Patient, SpecializationName, User } from '_api/types';
import { ACCOUNT_TYPE, AdminRouteMap, PatientRouteMap } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useMemo } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useNavigateTo } from 'utils';

import { useGetOrganizationByID } from '../../_api/queries/organizations';
import { TextButton } from '../atoms';

import { OrganizationDetailProps } from './types';

export const OrganizationDetail = ({ id }: OrganizationDetailProps) => {
  const page = 0;
  const size = 1000;
  const navigate = useNavigateTo();
  const { data: archivedPatients, isFetching: isArchivedPatientsLoading } =
    useGetArchivedPatientsInOrg(id, { page, size });
  const { data: archivedUsers, isFetching: isArchivedUsersLoading } =
    useGetArchivedUsersInOrg(id, { page, size });
  const { data: org } = useGetOrganizationByID(id);

  const orgName = useMemo(() => {
    return org ? org.name : '';
  }, [org]);

  const patientActionButtons = {
    viewButton: true,
    editButton: true,
  };

  // function handleEditUserOnClick(id: number | string) {
  //   navigate(UsersRouteMap.EDIT, id.toString());
  // }

  // function handleViewUserOnClick(id: number | string) {
  //   navigate(UsersRouteMap.DETAIL, id.toString());
  // }

  const renderRole = (user: User) => {
    if (
      user &&
      user.accountType === ACCOUNT_TYPE.THERAPIST &&
      user.therapistSpecializations
    ) {
      return user.therapistSpecializations
        .map((x: ConstantsNameDescriptions<SpecializationName>) => x.description)
        .join(',')
        .toUpperCase();
    }
    return user.accountType.split('_').join(' ');
  };

  const usersColumns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      },
      {
        header: 'Email',
        accessorFn: (row) => `${row.email}`,
      },
      {
        header: 'Hospital',
        accessorFn: (row) => `${row.hospital ? row.hospital.name : 'N/A'}`,
      },
      {
        header: 'Role',
        accessorFn: (row) => renderRole(row),
      },
    ],
    [archivedUsers],
  );

  function handleViewPatientOnClick(id: number) {
    navigate(PatientRouteMap.DETAIL, id.toString());
  }

  function handleEditPatientOnClick(id: number) {
    navigate(PatientRouteMap.EDIT, id.toString());
  }

  const patientsColumns = useMemo<MRT_ColumnDef<Patient>[]>(
    () => [
      {
        header: 'Child',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      },
      {
        header: 'Therapist',
        accessorFn: (row) =>
          `${row.therapists[0].firstName} ${row.therapists[0]?.lastName}`,
      },
      {
        header: 'Hospital',
        accessorFn: (row) => `${row.therapists[0]?.hospital?.name}`,
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'user'}
            viewOnClick={() => handleViewPatientOnClick(row.id)}
            editOnClick={() => handleEditPatientOnClick(row.id)}
            actionButtons={patientActionButtons}
          />
        ),
        size: 10,
      },
    ],
    [archivedPatients],
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Box>
        <TextButton onClick={() => navigate(AdminRouteMap.ARCHIVE_ORGANIZATIONS)}>
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      <Typography variant="h4" gutterBottom>
        {orgName}
      </Typography>
      <Typography variant="h6" gutterBottom>
        Archived Users
      </Typography>
      {!isArchivedUsersLoading ? (
        <MaterialReactTable
          columns={usersColumns}
          data={archivedUsers ? archivedUsers : []}
          enableSorting={true}
          enableColumnActions={false}
          enableTopToolbar={false}
          enablePagination={true}
        />
      ) : null}
      <Typography variant="h6" gutterBottom mt={4}>
        Archived Patients
      </Typography>
      {!isArchivedPatientsLoading ? (
        <MaterialReactTable
          columns={patientsColumns}
          data={archivedPatients ? archivedPatients : []}
          enableSorting={true}
          enableColumnActions={false}
          enableTopToolbar={false}
          enablePagination={true}
        />
      ) : null}
    </Box>
  );
};

export default OrganizationDetail;
